import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Exam, Category, User } from '../../types';
import * as db from '../../services/dataService';

interface HomePageProps {
    categories: Category[];
    exams: Exam[];
    user: User | null;
    isLoadingData: boolean;
    onStartExam: (examId: string) => void;
    onShowPricing: () => void;
    onDailyCAClick: () => void;
    onUpdateUser?: (user: User) => void;
}

const HomePage: React.FC<HomePageProps> = ({
    categories,
    exams,
    user,
    isLoadingData,
    onStartExam,
    onShowPricing,
    onDailyCAClick,
    onUpdateUser
}) => {
    const navigate = useNavigate();
    const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<'All' | 'Free' | 'Premium'>('All');

    // Onboarding Category Selection State
    const [selectedCatForOnboarding, setSelectedCatForOnboarding] = useState<string | null>(null);
    const [isOnboardingSaving, setIsOnboardingSaving] = useState(false);

    // Show onboarding if user is logged in, is not an admin, and has no allowed categories selected
    const showOnboarding = user && user.role === 'user' && (!user.allowedCategories || user.allowedCategories.length === 0);

    const handleSaveOnboarding = async () => {
        if (!selectedCatForOnboarding || !user || !onUpdateUser) return;
        setIsOnboardingSaving(true);
        try {
            const cat = categories.find(c => c.id === selectedCatForOnboarding);
            const targetExamName = cat ? cat.name : '';
            
            await db.updateUserProfile(user.id, {
                allowedCategories: [selectedCatForOnboarding],
                targetExam: targetExamName
            });

            const updatedUser: User = {
                ...user,
                allowedCategories: [selectedCatForOnboarding],
                targetExam: targetExamName
            };
            onUpdateUser(updatedUser);
        } catch (e) {
            console.error("Failed to save onboarding category", e);
            alert("Failed to save category preference. Please try again.");
        } finally {
            setIsOnboardingSaving(false);
        }
    };

    // Filter categories based on user allowedCategories
    const allowedCategories = React.useMemo(() => {
        if (!user || user.role === 'admin') return categories;
        if (!user.allowedCategories || user.allowedCategories.length === 0) return categories;
        return categories.filter(cat => user.allowedCategories?.includes(cat.id));
    }, [categories, user]);

    // Filter exams based on user allowedCategories
    const allowedExams = React.useMemo(() => {
        if (!user || user.role === 'admin') return exams;
        if (!user.allowedCategories || user.allowedCategories.length === 0) return exams;
        return exams.filter(exam => user.allowedCategories?.includes(exam.category));
    }, [exams, user]);

    // Daily CA Logic
    const today = new Date();
    const now = new Date();
    const dateString = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const dateISO = today.toISOString().split('T')[0];
    const defaultDailyExamId = `daily-ca-${dateISO}`;

    // === HERO BANNER: Only show exams with ACTIVE time limits ===
    // Scheduled exams = those with startTime AND endTime set
    const scheduledExams = allowedExams.filter(e => e.startTime && e.endTime);

    // Live exams = started and not yet ended (show in HERO)
    const activeLiveExams = scheduledExams
        .filter(e => new Date(e.startTime!) <= now && new Date(e.endTime!) > now)
        .slice(0, 4);

    // Upcoming exams = not yet started (show in HERO)
    const upcomingLiveExams = scheduledExams
        .filter(e => new Date(e.startTime!) > now)
        .slice(0, 4);

    // IDs of exams currently shown in hero (to exclude from category)
    const heroExamIds = new Set([
        ...activeLiveExams.map(e => e.id),
        ...upcomingLiveExams.map(e => e.id)
    ]);

    // === CATEGORY CARDS: Show exams that are NOT in hero ===
    // This includes: 1) No time limit, 2) Time limit expired
    const categoryExams = allowedExams.filter(e => !heroExamIds.has(e.id));

    // Filter exams based on search query and type filter
    const filteredCategoryExams = allowedExams.filter(exam => {
        const matchesSearch = searchQuery 
            ? exam.title.toLowerCase().includes(searchQuery.toLowerCase())
            : !heroExamIds.has(exam.id);

        const matchesType = typeFilter === 'All'
            ? true
            : typeFilter === 'Premium'
                ? exam.isPremium
                : !exam.isPremium;

        return matchesSearch && matchesType;
    });

    // Stats for hero
    const totalQuestions = allowedExams.reduce((acc, e) => acc + (e.questions?.length || e.totalQuestions || 0), 0);

    return (
        <>
            {/* Premium Hero Section with Mesh Gradient */}
            <div className="relative overflow-hidden">
                {/* Animated Background */}
                <div className="absolute inset-0 animated-gradient"></div>
                <div className="absolute inset-0 mesh-gradient"></div>

                {/* Floating Elements */}
                <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl animate-float"></div>
                <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }}></div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
                    <div className="lg:flex justify-between items-center gap-16">
                        {/* Hero Content */}
                        <div className="lg:w-1/2 mb-12 lg:mb-0 animate-slide-up">
                            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm text-white/80 mb-6 border border-white/10">
                                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                                India's #1 Free Exam Prep Platform
                            </div>

                            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                                Master Your Exam with{' '}
                                <span className="relative">
                                    <span className="gradient-text">ThrustED</span>
                                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                                        <path d="M2 6C50 2 150 2 198 6" stroke="url(#gradient)" strokeWidth="4" strokeLinecap="round" />
                                        <defs>
                                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                <stop offset="0%" stopColor="#f85a40" />
                                                <stop offset="100%" stopColor="#a855f7" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                </span>
                            </h1>

                            <p className="text-lg md:text-xl text-white/70 mb-8 max-w-xl leading-relaxed">
                                Smart analytics, no-repeat questions, daily current affairs, and AI-powered insights to ace UPSC, APPSC, Banking & more.
                            </p>

                            <div className="flex flex-wrap gap-4 mb-12">
                                <button
                                    onClick={onDailyCAClick}
                                    className="btn-primary text-white px-8 py-4 rounded-2xl font-bold text-lg inline-flex items-center group"
                                >
                                    <i className="fas fa-play-circle mr-3 group-hover:scale-110 transition-transform"></i>
                                    Start Free Mock
                                </button>
                                <button
                                    onClick={onShowPricing}
                                    className="glass text-slate-800 dark:text-white px-8 py-4 rounded-2xl font-bold text-lg inline-flex items-center hover:bg-slate-100/40 dark:hover:bg-white/20 transition-all"
                                >
                                    <i className="fas fa-crown mr-3 text-yellow-400"></i>
                                    View Premium Plans
                                </button>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-6">
                                <div className="text-center">
                                    <div className="text-3xl md:text-4xl font-bold text-white">{totalQuestions.toLocaleString()}+</div>
                                    <div className="text-sm text-white/50 mt-1">Questions</div>
                                </div>
                                <div className="text-center border-x border-white/10">
                                    <div className="text-3xl md:text-4xl font-bold text-white">{exams.length}+</div>
                                    <div className="text-sm text-white/50 mt-1">Mock Tests</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl md:text-4xl font-bold text-white">10k+</div>
                                    <div className="text-sm text-white/50 mt-1">Students</div>
                                </div>
                            </div>
                        </div>

                        {/* Live Exams Section in Hero - Show all scheduled exams */}
                        <div className="lg:w-5/12 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                            {/* If there are active live exams, show them */}
                            {activeLiveExams.length > 0 ? (
                                <div className="space-y-4">
                                    {activeLiveExams.slice(0, 2).map((exam, idx) => (
                                        <div
                                            key={exam.id}
                                            className="glass rounded-3xl p-6 text-slate-800 dark:text-white relative overflow-hidden group cursor-pointer transform hover:scale-[1.02] transition-all duration-300 border-2 border-green-500/50"
                                            onClick={() => onStartExam(exam.id)}
                                        >
                                            {/* Glow Effect */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-emerald-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                            {/* Live Badge */}
                                            <div className="absolute top-4 right-4">
                                                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg shadow-green-500/30 flex items-center animate-pulse">
                                                    <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                                                    LIVE NOW
                                                </div>
                                            </div>

                                            <div className="relative z-10">
                                                <div className="flex items-center mb-4">
                                                    <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center text-white text-xl mr-4 shadow-lg shadow-green-500/30">
                                                        <i className="fas fa-bolt"></i>
                                                    </div>
                                                    <div>
                                                        <h3 className="font-display font-bold text-xl pr-20">{exam.title}</h3>
                                                        <p className="text-slate-500 dark:text-white/60 text-sm">{exam.totalQuestions} Questions • {exam.durationMinutes} mins</p>
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center text-green-650 dark:text-green-400 font-semibold text-sm">
                                                        <i className="fas fa-clock mr-2"></i>
                                                        Ends: {new Date(exam.endTime!).toLocaleTimeString()}
                                                    </div>
                                                    <button className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-5 py-2.5 rounded-xl font-bold text-sm group-hover:bg-gradient-to-r group-hover:from-green-500 group-hover:to-emerald-500 group-hover:text-white transition-all shadow-lg">
                                                        Attempt Now <i className="fas fa-arrow-right ml-2"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : upcomingLiveExams.length > 0 ? (
                                /* Show upcoming exams if no active live */
                                <div className="space-y-4">
                                    {upcomingLiveExams.slice(0, 2).map((exam, idx) => (
                                        <div
                                            key={exam.id}
                                            className="glass rounded-3xl p-6 text-slate-800 dark:text-white relative overflow-hidden group"
                                        >
                                            {/* Upcoming Badge */}
                                            <div className="absolute top-4 right-4">
                                                <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg shadow-orange-500/30 flex items-center">
                                                    <i className="fas fa-calendar-alt mr-2"></i>
                                                    UPCOMING
                                                </div>
                                            </div>

                                            <div className="relative z-10">
                                                <div className="flex items-center mb-4">
                                                    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl flex items-center justify-center text-white text-xl mr-4 shadow-lg shadow-orange-500/30">
                                                        <i className="fas fa-hourglass-half"></i>
                                                    </div>
                                                    <div>
                                                        <h3 className="font-display font-bold text-xl pr-20">{exam.title}</h3>
                                                        <p className="text-slate-500 dark:text-white/60 text-sm">{exam.totalQuestions} Questions • {exam.durationMinutes} mins</p>
                                                    </div>
                                                </div>

                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center text-orange-600 dark:text-orange-400 font-semibold text-sm">
                                                        <i className="fas fa-calendar mr-2"></i>
                                                        Starts: {new Date(exam.startTime!).toLocaleString()}
                                                    </div>
                                                    <span className="bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/70 px-5 py-2.5 rounded-xl font-bold text-sm cursor-not-allowed">
                                                        <i className="far fa-clock mr-2"></i> Waiting
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                /* Default: Show Daily CA Card if no scheduled exams */
                                <div
                                    className="glass rounded-3xl p-8 text-slate-800 dark:text-white relative overflow-hidden group cursor-pointer transform hover:scale-[1.02] transition-all duration-300"
                                    onClick={onDailyCAClick}
                                >
                                    {/* Glow Effect */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                    {/* Live Badge */}
                                    <div className="absolute top-6 right-6">
                                        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg shadow-orange-500/30 flex items-center">
                                            <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                                            LIVE NOW
                                        </div>
                                    </div>

                                    <div className="relative z-10">
                                        <div className="flex items-center mb-6">
                                            <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center text-white text-2xl mr-5 shadow-lg shadow-orange-500/30">
                                                <i className="fas fa-calendar-day"></i>
                                            </div>
                                            <div>
                                                <h3 className="font-display font-bold text-2xl">Daily Current Affairs</h3>
                                                <p className="text-slate-500 dark:text-white/60">{dateString} • 30 Questions</p>
                                            </div>
                                        </div>

                                        <p className="text-slate-600 dark:text-white/70 mb-8 text-base leading-relaxed">
                                            Stay updated with curated current affairs quiz. Free forever, updated daily for UPSC, APPSC & Banking.
                                        </p>

                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center text-orange-650 dark:text-orange-400 font-semibold">
                                                <i className="fas fa-clock mr-2"></i>
                                                15 minutes
                                            </div>
                                            <button className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-3 rounded-xl font-bold group-hover:bg-gradient-to-r group-hover:from-orange-500 group-hover:to-red-500 group-hover:text-white transition-all shadow-lg">
                                                Start Quiz <i className="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Categories - New Modern Design with Tabs */}
            <div id="categories-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center mb-10">
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                        Explore <span className="gradient-text">Exam Categories</span>
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                        Choose your target exam and start practicing with curated mock tests
                    </p>
                </div>

                {/* Search & Filter Bar */}
                <div className="max-w-4xl mx-auto mb-10 bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-4 shadow-glass flex flex-col md:flex-row gap-4 animate-slide-up">
                    <div className="flex-1 relative">
                        <i className="fas fa-search absolute left-4 top-3.5 text-slate-455 dark:text-slate-400"></i>
                        <input
                            type="text"
                            placeholder="Search mock exams (e.g. Civil Services, Daily CA, APPSC...)"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-4 top-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                            >
                                <i className="fas fa-times-circle"></i>
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value as any)}
                            className="px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-2xl bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 text-sm outline-none focus:ring-2 focus:ring-orange-500"
                        >
                            <option value="All">All Types</option>
                            <option value="Free">Free Mocks</option>
                            <option value="Premium">Premium Pro</option>
                        </select>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex justify-center mb-10">
                    <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-2xl p-1.5 gap-1">
                        <button
                            onClick={() => setExpandedCategoryId(null)}
                            className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all ${expandedCategoryId === null
                                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                                : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700'
                                }`}
                        >
                            All Exams
                        </button>
                        {allowedCategories.slice(0, 5).map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => {
                                    if (cat.id === 'ca') {
                                        onDailyCAClick();
                                    } else {
                                        setExpandedCategoryId(expandedCategoryId === cat.id ? null : cat.id);
                                    }
                                }}
                                className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-all hidden md:block ${expandedCategoryId === cat.id
                                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                                    : 'text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700'
                                    }`}
                            >
                                {cat.name}
                            </button>
                        ))}
                    </div>
                </div>

                {isLoadingData ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-6 animate-pulse">
                                <div className="flex items-start gap-4">
                                    <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-2xl"></div>
                                    <div className="flex-1">
                                        <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
                                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (searchQuery || typeFilter !== 'All') ? (
                    /* Search Results Grid */
                    <div className="space-y-6 animate-fade-in">
                        <div className="flex justify-between items-center mb-6">
                            <h4 className="text-lg font-bold text-slate-800 dark:text-slate-200">
                                Search Results ({filteredCategoryExams.length})
                            </h4>
                            <button 
                                onClick={() => { setSearchQuery(''); setTypeFilter('All'); }}
                                className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-semibold text-sm hover:underline"
                            >
                                Clear Search
                            </button>
                        </div>
                        {filteredCategoryExams.length === 0 ? (
                            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 text-center py-16 text-slate-500 dark:text-slate-400 shadow-glass">
                                <i className="fas fa-search text-5xl mb-4 text-slate-300 dark:text-slate-600"></i>
                                <p className="text-lg font-semibold text-slate-700 dark:text-slate-300">No mock exams found</p>
                                <p className="text-sm mt-1">Try adjusting your keywords or type filter.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredCategoryExams.map(exam => {
                                    const examCategory = categories.find(c => c.id === exam.category);
                                    return (
                                        <div key={exam.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 card-hover flex flex-col justify-between shadow-glass">
                                            <div>
                                                <div className="flex justify-between items-start mb-3">
                                                    <span className="bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                                                        {examCategory?.name || 'Mock'}
                                                    </span>
                                                    {exam.isPremium ? (
                                                        <span className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[10px] px-2.5 py-1 rounded-full font-bold flex items-center">
                                                            <i className="fas fa-crown mr-1 animate-pulse-slow"></i> PRO
                                                        </span>
                                                    ) : (
                                                        <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 text-[10px] px-2.5 py-1 rounded-full font-bold">FREE</span>
                                                    )}
                                                </div>
                                                <h4 className="font-bold text-slate-900 dark:text-white text-base mb-2 line-clamp-2">{exam.title}</h4>
                                                <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 mb-4 space-x-3">
                                                    <span><i className="far fa-question-circle mr-1 text-slate-400"></i> {exam.totalQuestions} Questions</span>
                                                    <span><i className="far fa-clock mr-1 text-slate-400"></i> {exam.durationMinutes} mins</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => onStartExam(exam.id)}
                                                className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all ${exam.isPremium
                                                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:opacity-95'
                                                    : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:opacity-95'
                                                    }`}
                                            >
                                                <i className={`fas ${exam.isPremium ? 'fa-unlock' : 'fa-play'} mr-2`}></i>
                                                {exam.isPremium ? 'Unlock Test' : 'Start Test'}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Show filtered categories based on selected tab */}
                        {(expandedCategoryId ? allowedCategories.filter(c => c.id === expandedCategoryId) : allowedCategories).map((cat) => {
                            const catExams = categoryExams.filter(e => e.category === cat.id);
                            const examCount = catExams.length;
                            const totalQuestionCount = catExams.reduce((acc, e) => acc + (e.totalQuestions || 0), 0);

                            return (
                                <div
                                    key={cat.id}
                                    className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 card-hover group cursor-pointer relative overflow-hidden"
                                    onClick={() => {
                                        if (cat.id === 'ca') {
                                            onDailyCAClick();
                                        } else {
                                            setExpandedCategoryId(expandedCategoryId === cat.id ? cat.id : cat.id);
                                        }
                                    }}
                                >
                                    {/* Test Count Badge */}
                                    <div className="absolute top-4 right-4">
                                        <span className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-bold px-3 py-1.5 rounded-lg">
                                            {examCount} Tests
                                        </span>
                                    </div>

                                    {/* Icon and Title */}
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-2xl flex items-center justify-center text-2xl text-orange-600 dark:text-orange-400 group-hover:from-orange-500 group-hover:to-red-500 group-hover:text-white transition-all duration-300 shadow-sm">
                                            <i className={`fas ${cat.icon}`}></i>
                                        </div>
                                        <div className="flex-1 pr-16">
                                            <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                                                {cat.name}
                                            </h3>
                                            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                                                {totalQuestionCount > 0 ? `${totalQuestionCount.toLocaleString()}+ practice questions` : 'Practice questions available'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Rating and Explore */}
                                    <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700">
                                        <div className="flex items-center gap-1">
                                            <div className="flex text-orange-400">
                                                <i className="fas fa-star text-xs"></i>
                                                <i className="fas fa-star text-xs"></i>
                                                <i className="fas fa-star text-xs"></i>
                                                <i className="fas fa-star text-xs"></i>
                                                <i className="fas fa-star-half-alt text-xs"></i>
                                            </div>
                                            <span className="text-slate-600 dark:text-slate-400 text-sm font-semibold ml-1">4.9</span>
                                        </div>
                                        <button className="text-orange-600 dark:text-orange-400 font-semibold text-sm flex items-center group-hover:translate-x-1 transition-transform">
                                            Explore <i className="fas fa-arrow-right ml-2 text-xs"></i>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Expanded Exams List - Show when a category is selected */}
                {expandedCategoryId && (
                    <div className="mt-10">
                        {(() => {
                            const category = allowedCategories.find(c => c.id === expandedCategoryId);
                            const expandedCatExams = categoryExams.filter(e => e.category === expandedCategoryId);

                            if (expandedCatExams.length === 0) {
                                return (
                                    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center py-12 text-slate-500 dark:text-slate-400">
                                        <i className="far fa-folder-open text-5xl mb-4 text-slate-300 dark:text-slate-600"></i>
                                        <p className="text-lg">No exams currently available for {category?.name}.</p>
                                        <p className="text-sm mt-2">Check back soon!</p>
                                    </div>
                                );
                            }

                            return (
                                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                    <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 flex justify-between items-center">
                                        <h3 className="font-display font-bold text-white text-lg flex items-center">
                                            <i className={`fas ${category?.icon} mr-3`}></i>
                                            {category?.name} - Available Tests
                                        </h3>
                                        <span className="bg-white/20 text-white text-sm font-bold px-3 py-1 rounded-lg">
                                            {expandedCatExams.length} Tests
                                        </span>
                                    </div>
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {expandedCatExams.map(exam => (
                                                <div key={exam.id} className="bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700 p-5 card-hover">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <h5 className="font-bold text-slate-900 dark:text-white line-clamp-2 flex-1">{exam.title}</h5>
                                                        {exam.isPremium ? (
                                                            <span className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs px-2 py-1 rounded-full font-bold flex items-center ml-2 whitespace-nowrap">
                                                                <i className="fas fa-crown mr-1"></i> PRO
                                                            </span>
                                                        ) : (
                                                            <span className="bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 text-xs px-2 py-1 rounded-full font-bold ml-2">FREE</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-4 space-x-4">
                                                        <span className="flex items-center"><i className="far fa-question-circle mr-1"></i> {exam.totalQuestions} Qs</span>
                                                        <span className="flex items-center"><i className="far fa-clock mr-1"></i> {exam.durationMinutes} mins</span>
                                                    </div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onStartExam(exam.id); }}
                                                        className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all ${exam.isPremium
                                                            ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white'
                                                            : 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                                                            }`}
                                                    >
                                                        <i className={`fas ${exam.isPremium ? 'fa-unlock' : 'fa-play'} mr-2`}></i>
                                                        {exam.isPremium ? 'Unlock Test' : 'Start Test'}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>


            {/* Premium Footer */}
            <footer className="bg-slate-900 text-white py-16 relative overflow-hidden">
                <div className="absolute inset-0 mesh-gradient opacity-30"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                        <div>
                            <div className="flex items-center mb-6">
                                <div className="text-3xl mr-3">🚀</div>
                                <span className="font-display font-bold text-2xl gradient-text">ThrustED</span>
                            </div>
                            <p className="text-slate-400 leading-relaxed">Empowering aspirants with free, high-quality resources to crack competitive exams.</p>
                        </div>
                        <div>
                            <h4 className="font-display font-bold text-lg mb-6">Quick Links</h4>
                            <ul className="space-y-3">
                                <li><button onClick={onDailyCAClick} className="text-slate-400 hover:text-orange-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-orange-500/50"></i> Daily Current Affairs</button></li>
                                <li><button onClick={() => { setExpandedCategoryId('upsc'); document.querySelector('#categories-section')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-slate-400 hover:text-orange-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-orange-500/50"></i> UPSC Prelims</button></li>
                                <li><button onClick={() => { setExpandedCategoryId('banking'); document.querySelector('#categories-section')?.scrollIntoView({ behavior: 'smooth' }); }} className="text-slate-400 hover:text-orange-500 transition-colors flex items-center"><i className="fas fa-chevron-right text-xs mr-2 text-orange-500/50"></i> Banking Mocks</button></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-display font-bold text-lg mb-6">Company</h4>
                            <ul className="space-y-3">
                                <li><span className="text-slate-400 flex items-center cursor-default"><i className="fas fa-chevron-right text-xs mr-2 text-orange-500/50"></i> About Us</span></li>
                                <li><span className="text-slate-400 flex items-center cursor-default"><i className="fas fa-chevron-right text-xs mr-2 text-orange-500/50"></i> Privacy Policy</span></li>
                                <li><span className="text-slate-400 flex items-center cursor-default"><i className="fas fa-chevron-right text-xs mr-2 text-orange-500/50"></i> Terms of Service</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-display font-bold text-lg mb-6">Connect With Us</h4>
                            <div className="flex space-x-4">
                                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-slate-800 hover:bg-orange-500 rounded-xl flex items-center justify-center transition-all hover:scale-110"><i className="fab fa-twitter text-lg"></i></a>
                                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-slate-800 hover:bg-orange-500 rounded-xl flex items-center justify-center transition-all hover:scale-110"><i className="fab fa-instagram text-lg"></i></a>
                                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-slate-800 hover:bg-orange-500 rounded-xl flex items-center justify-center transition-all hover:scale-110"><i className="fab fa-youtube text-lg"></i></a>
                                <a href="https://t.me" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-slate-800 hover:bg-orange-500 rounded-xl flex items-center justify-center transition-all hover:scale-110"><i className="fab fa-telegram text-lg"></i></a>
                            </div>
                        </div>
                    </div>
                    <div className="mt-16 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
                        © 2025 ThrustED. All rights reserved. Built with ❤️ for aspirants.
                    </div>
                </div>
            </footer>

            {/* Premium Category Onboarding Modal */}
            {showOnboarding && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/75 backdrop-blur-md animate-fade-in">
                    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-glass-lg w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-700 p-8 relative animate-scale-in">
                        
                        {/* Top decorative gradient bar */}
                        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-purple-500"></div>

                        <div className="text-center mb-8 mt-2">
                            <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-2xl flex items-center justify-center text-3xl mx-auto mb-5 shadow-lg shadow-orange-500/30">
                                <i className="fas fa-bullseye"></i>
                            </div>
                            <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-white">
                                Choose Your Target Category
                            </h2>
                            <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm max-w-md mx-auto">
                                To customize your learning and exam experience, select your primary exam category. General users have access to exams under their selected category only.
                            </p>
                        </div>

                        {/* Grid of categories */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                            {categories.map(cat => {
                                const isSelected = selectedCatForOnboarding === cat.id;
                                return (
                                    <div
                                        key={cat.id}
                                        onClick={() => setSelectedCatForOnboarding(cat.id)}
                                        className={`p-5 rounded-2xl border-2 cursor-pointer transition-all flex flex-col items-center justify-center text-center gap-3 relative overflow-hidden group ${
                                            isSelected
                                                ? 'border-orange-500 bg-orange-50/40 dark:bg-orange-950/20'
                                                : 'border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900 hover:border-orange-200 dark:hover:border-orange-900/30'
                                        }`}
                                    >
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-lg shadow-sm transition-all ${
                                            isSelected
                                                ? 'bg-orange-500 text-white'
                                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:bg-orange-100 group-hover:text-orange-650'
                                        }`}>
                                            <i className={`fas ${cat.icon}`}></i>
                                        </div>
                                        <span className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                                            {cat.name}
                                        </span>
                                        
                                        {isSelected && (
                                            <div className="absolute top-2 right-2 w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-[10px]">
                                                <i className="fas fa-check"></i>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col items-center">
                            <button
                                onClick={handleSaveOnboarding}
                                disabled={!selectedCatForOnboarding || isOnboardingSaving}
                                className="w-full sm:w-auto px-10 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-2xl hover:opacity-95 shadow-lg shadow-orange-500/25 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-all text-base"
                            >
                                {isOnboardingSaving ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin mr-2"></i> Unlocking Dashboard...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-unlock mr-2"></i> Confirm and Continue
                                    </>
                                )}
                            </button>
                            <p className="text-slate-400 dark:text-slate-500 text-[10px] mt-3 italic">
                                * Allowed category updates require administrator approval.
                            </p>
                        </div>

                    </div>
                </div>
            )}
        </>
    );
};

export default HomePage;
